/**
 * Stable, unique React key for an Audit grid row.
 *
 * Two row shapes flow into the merged Audit grid:
 *  - saved/accepted rows carry `lksi_rev_and_rollseasonsid`, the true per-row primary
 *    key (unique across ALL franchises). Note: `seasonid` alone is NOT unique -- e.g.
 *    the known Nittany duplicate is two accepted rows that share seasonid 263 and
 *    skey "0-522-263", differing only in end date. Keying on skey/seasonID collides
 *    for that pair and lets a stale row survive a franchise-filter change.
 *  - new/unsaved rows have no `lksi_rev_and_rollseasonsid` yet (not persisted); they
 *    carry `warehouseSeasonID` = "season_dsr_sam_<com_code>_<seasonid>", which is
 *    franchise-scoped and therefore unique per new season.
 *
 * Each branch is namespaced ("saved-" / "new-") so a saved id can never collide with a
 * new id that happens to share the same numeric seasonid.
 */
export function auditRowKey(seasonObj) {
  if (seasonObj == null) return 'row-unknown';

  const savedId = seasonObj.lksi_rev_and_rollseasonsid;
  if (savedId !== null && savedId !== undefined && savedId !== '') {
    return `saved-${savedId}`;
  }

  const warehouseId = seasonObj.warehouseSeasonID;
  if (warehouseId) {
    return `new-${warehouseId}`;
  }

  // Defensive fallback (no canonical id present): include seasonDateE, which
  // disambiguates the duplicate class (same skey/seasonid, different end date) so
  // keys stay distinct even in the degenerate case.
  return `row-${seasonObj.sKey ?? ''}-${seasonObj.seasonID ?? ''}-${seasonObj.seasonDateE ?? ''}`;
}

/**
 * Stable key for a prior-season entry in an AuditRow sub-list.
 *
 * Prior-season display objects carry neither `lksi_rev_and_rollseasonsid` nor
 * `warehouseSeasonID`, so they can't use auditRowKey(). `seasonID + seasonName`
 * (the old key) still collides for the duplicate class -- same seasonid and name,
 * different dates (e.g. the Nittany pair). Include com_code and both dates so
 * entries stay distinct within, and across, franchises.
 */
export function priorSeasonKey(pseason) {
  if (pseason == null) return 'prior-unknown';
  return `prior-${pseason.comCode ?? ''}-${pseason.seasonID ?? ''}-${pseason.seasonDateS ?? ''}-${pseason.seasonDateE ?? ''}`;
}
